import { useState, useEffect } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { Link } from 'react-router-dom';
import { Globe, Shield, Users, Quote, Mail, Award, ChevronLeft, ChevronRight } from 'lucide-react';

// Angolan Landscapes for Hero Slideshow
const ANGOLA_LANDSCAPES = [
  {
    url: "https://images.unsplash.com/photo-1732027198030-d9ead6d14d4f?auto=format&fit=crop&w=1920&q=80",
    title: "Pôr do Sol Africano",
    description: "A beleza natural de Angola"
  },
  {
    url: "https://images.pexels.com/photos/32341372/pexels-photo-32341372.jpeg?auto=compress&cs=tinysrgb&w=1920",
    title: "Savana Angolana",
    description: "Paisagens únicas de Angola"
  },
  {
    url: "https://images.unsplash.com/photo-1760199078320-18976d421338?auto=format&fit=crop&w=1920&q=80",
    title: "Embondeiro ao Entardecer",
    description: "A majestade da natureza angolana"
  },
  {
    url: "https://images.pexels.com/photos/16332790/pexels-photo-16332790.jpeg?auto=compress&cs=tinysrgb&w=1920",
    title: "Luanda Moderna",
    description: "O desenvolvimento de Angola"
  },
  {
    url: "https://images.unsplash.com/photo-1743172086091-b95acd708315?auto=format&fit=crop&w=1920&q=80",
    title: "Baía de Luanda",
    description: "A capital de Angola"
  },
];

// Current Ambassador
const AMBASSADOR = {
  name: "S.E. Balbina Malheiros da Silva",
  title: "Embaixadora Extraordinária e Plenipotenciária",
  photo: "https://customer-assets.emergentagent.com/job_792e387f-b4b3-43c6-bf9d-46ac5c601086/artifacts/ehbg8met_EmbajadoraES.png",
  biography: `Balbina Malheiros Dias da Silva (Luanda, 27 de fevereiro de 1959) é diplomata de carreira da República de Angola, com uma trajetória consolidada no serviço externo angolano e vasta experiência em política externa, cooperação internacional e representação diplomática.

Formação Académica

Entre 1981 e 1982, concluiu o curso de Relações Exteriores na Escola Nacional do Partido. Posteriormente, frequentou a Faculdade de Direito da Universidade Agostinho Neto, onde estudou até 1984.
Em 1990, graduou-se em Ciência Política e Diplomacia pelo Instituto Rio Branco, em Brasília, instituição de referência na formação diplomática.

Carreira Diplomática

Ingressou no serviço diplomático angolano em 1983, tendo iniciado funções como adida diplomática na Direção para a América Latina e Caribe do Ministério das Relações Exteriores.

Posteriormente, assumiu a chefia da Divisão do Norte de África e Médio Oriente, acumulando responsabilidades no acompanhamento das atividades da Organização da Unidade Africana.

Em 1993, foi nomeada Segunda-Secretária na Embaixada de Angola em Harare, República do Zimbábue.
Em 1998, assumiu responsabilidades pelos Assuntos da África Austral no Ministério das Relações Exteriores, e, no ano 2000, foi designada Chefe do Departamento da Europa Central e Oriental.

Três anos mais tarde, foi nomeada Diretora do Departamento para as Américas, função estratégica no quadro da diplomacia angolana.

Entre 2012 e 2019, exerceu o cargo de Embaixadora Extraordinária e Plenipotenciária da República de Angola na República da Zâmbia.

Missão na Alemanha e Nova Nomeação

Em 24 de outubro de 2019, foi acreditada como Embaixadora Extraordinária e Plenipotenciária da República de Angola na República Federal da Alemanha, tornando-se a primeira mulher angolana a chefiar a missão diplomática em Berlim. Durante o seu mandato, acumulou igualmente a função de Embaixadora não residente na República Checa.

Em 7 de fevereiro de 2025, por decisão de Sua Excelência o Presidente da República, João Manuel Gonçalves Lourenço, foi exonerada das funções na Alemanha e nomeada para o cargo de Embaixadora Extraordinária e Plenipotenciária da República de Angola no Reino de Espanha.

Perfil

Diplomata experiente e multilíngue, fala português, francês, inglês e espanhol.
É casada e tem dedicado mais de quatro décadas ao serviço da diplomacia angolana, contribuindo para o fortalecimento das relações bilaterais e multilaterais de Angola em diversos continentes.`,
  message: `"É com grande honra e responsabilidade que represento a República de Angola no Reino de Espanha. A nossa missão é fortalecer os laços de amizade e cooperação entre os nossos dois países, ao mesmo tempo que servimos e apoiamos a comunidade angolana residente em Espanha.

A Embaixada está empenhada em prestar serviços consulares de excelência, promover a cultura angolana e facilitar o desenvolvimento das relações económicas e comerciais entre Angola e Espanha.

Convido todos os cidadãos angolanos a utilizarem os serviços da Embaixada e a participarem nas atividades que organizamos ao longo do ano. Juntos, construímos uma comunidade mais forte e unida."`
};

// Former Ambassadors (from oldest to most recent)
const FORMER_AMBASSADORS = [
  {
    name: "Fernando José de França Dias Van-Dúnem",
    period: "1982 – 1985",
    photo: "https://customer-assets.emergentagent.com/job_792e387f-b4b3-43c6-bf9d-46ac5c601086/artifacts/5lhz0pi8_Fernando_Jose%CC%81_de_Franc%CC%A7a_Dias_Van-Du%CC%81nem_%281980%29.jpg",
    note: "Primeiro Embaixador de Angola em Espanha"
  },
  {
    name: "Mawete João Baptista",
    period: "1986 – 1989",
    photo: "https://customer-assets.emergentagent.com/job_792e387f-b4b3-43c6-bf9d-46ac5c601086/artifacts/sze57l9l_Mawete%20Joa%CC%83o%20Baptista.png",
    note: "Consolidação das relações diplomáticas"
  },
  {
    name: "Kamu de Almeida",
    period: "1989 – 1993",
    photo: "https://customer-assets.emergentagent.com/job_792e387f-b4b3-43c6-bf9d-46ac5c601086/artifacts/p23i3eve_Kamu%20de%20Almeida.png",
    note: "Fortalecimento institucional"
  },
  {
    name: "Assunção dos Anjos",
    period: "1993 – 2000",
    photo: "https://customer-assets.emergentagent.com/job_792e387f-b4b3-43c6-bf9d-46ac5c601086/artifacts/rmljavrg_assuncao%20dos%20anjos.jpg",
    note: "Período de transição democrática"
  },
  {
    name: "General Pedro Sebastião",
    period: "2000 – 2003",
    photo: "https://customer-assets.emergentagent.com/job_792e387f-b4b3-43c6-bf9d-46ac5c601086/artifacts/5wv3wp64_General%20Pedro%20Sebastia%CC%83o.jpeg",
    note: "Consolidação da paz"
  },
  {
    name: "Armando da Cruz Neto",
    period: "2004 – 2006",
    photo: "https://customer-assets.emergentagent.com/job_792e387f-b4b3-43c6-bf9d-46ac5c601086/artifacts/w5sh9iby_General%20Armando%20da%20Cruz.jpeg",
    note: "Fortalecimento das relações económicas"
  },
  {
    name: "Victor Manuel Rita da Fonseca Lima",
    period: "2006 – 2017",
    photo: "https://customer-assets.emergentagent.com/job_792e387f-b4b3-43c6-bf9d-46ac5c601086/artifacts/kwuw8m5n_Victor%20Lima.jpeg",
    note: "Modernização dos serviços consulares"
  },
  {
    name: "José Luís de Matos Agostinho",
    period: "2018 – 2023",
    photo: "https://customer-assets.emergentagent.com/job_792e387f-b4b3-43c6-bf9d-46ac5c601086/artifacts/4rfvpc15_Jose%CC%81%20Lui%CC%81s%20de%20Matos%20Agostinho.jpeg",
    note: "Digitalização e aproximação à comunidade"
  },
  {
    name: "Alfredo Dombe",
    period: "2023 – 2025",
    photo: "https://customer-assets.emergentagent.com/job_792e387f-b4b3-43c6-bf9d-46ac5c601086/artifacts/y1ilc7j5_Alfredo%20Dombe.jpeg",
    note: "Reforço das relações bilaterais"
  },
];

// Landscape Slideshow Component for Hero
function LandscapeSlideshow() {
  const [current, setCurrent] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrent(prev => (prev + 1) % ANGOLA_LANDSCAPES.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);
  
  return (
    <div className="relative w-full h-full overflow-hidden rounded-xl">
      {ANGOLA_LANDSCAPES.map((landscape, idx) => (
        <div key={idx} 
          className={`absolute inset-0 transition-opacity duration-1000 ${idx === current ? 'opacity-100' : 'opacity-0'}`}>
          <img src={landscape.url} alt={landscape.title} 
            className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          <div className="absolute bottom-4 left-4 text-white">
            <p className="text-sm font-semibold">{landscape.title}</p>
            <p className="text-xs text-white/70">{landscape.description}</p>
          </div>
        </div>
      ))}
      {/* Dots indicator */}
      <div className="absolute bottom-4 right-4 flex gap-1.5">
        {ANGOLA_LANDSCAPES.map((_, idx) => (
          <button key={idx} onClick={() => setCurrent(idx)}
            className={`w-2 h-2 rounded-full transition-all ${idx === current ? 'bg-[#FFD700] w-4' : 'bg-white/50'}`} />
        ))}
      </div>
    </div>
  );
}

// Former Ambassadors Slideshow Component
function FormerAmbassadorsSlideshow() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying) return;
    const timer = setInterval(() => {
      setCurrentIndex(i => (i + 1) % Math.ceil(FORMER_AMBASSADORS.length / 3));
    }, 5000);
    return () => clearInterval(timer);
  }, [isAutoPlaying]);

  const totalSlides = Math.ceil(FORMER_AMBASSADORS.length / 3);
  const visibleAmbassadors = FORMER_AMBASSADORS.slice(currentIndex * 3, currentIndex * 3 + 3);

  const goTo = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const prev = () => goTo((currentIndex - 1 + totalSlides) % totalSlides);
  const next = () => goTo((currentIndex + 1) % totalSlides);

  return (
    <div className="relative" data-testid="former-ambassadors-slideshow">
      {/* Navigation Arrows */}
      <button onClick={prev} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-10 h-10 bg-white shadow-lg rounded-full flex items-center justify-center text-gray-600 hover:text-[#CC0000] hover:shadow-xl transition-all hidden md:flex">
        <ChevronLeft size={20} />
      </button>
      <button onClick={next} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-10 h-10 bg-white shadow-lg rounded-full flex items-center justify-center text-gray-600 hover:text-[#CC0000] hover:shadow-xl transition-all hidden md:flex">
        <ChevronRight size={20} />
      </button>

      {/* Ambassadors Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 transition-all duration-500">
        {visibleAmbassadors.map((amb, idx) => (
          <div key={amb.name} className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
            <div className="relative h-48 bg-gray-100">
              <img src={amb.photo} alt={amb.name} className="w-full h-full object-cover" />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                <span className="text-xs text-[#FFD700] font-semibold">{amb.period}</span>
              </div>
            </div>
            <div className="p-4">
              <h4 className="font-bold text-gray-900 text-sm mb-1">{amb.name}</h4>
              <p className="text-xs text-gray-500">{amb.note}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Dots */}
      <div className="flex justify-center gap-2 mt-6">
        {[...Array(totalSlides)].map((_, i) => (
          <button key={i} onClick={() => goTo(i)}
            className={`h-2 rounded-full transition-all ${i === currentIndex ? 'w-8 bg-[#CC0000]' : 'w-2 bg-gray-300 hover:bg-gray-400'}`} />
        ))}
      </div>
    </div>
  );
}

export default function About() {
  const { t } = useLanguage();
  return (
    <div className="min-h-screen bg-white">
      {/* Hero with Landscape Slideshow */}
      <div className="bg-[#0A0A0A] text-white border-b-4 border-[#FFD700]">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <p className="text-xs text-[#FFD700] font-semibold uppercase tracking-widest mb-3">{t('about.republic')}</p>
              <h1 className="text-3xl md:text-4xl font-bold mb-4">{t('about.title')}</h1>
              <p className="text-gray-300 text-sm leading-relaxed mb-6">
                {t('about.description')}
              </p>
              <Link to="/contactos" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#CC0000] text-white font-medium rounded-md hover:bg-[#A30000] transition-colors text-sm">
                {t('about.contact_embassy')}
              </Link>
            </div>
            <div className="h-64 lg:h-80">
              <LandscapeSlideshow />
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="bg-[#CC0000] text-white">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6 text-center">
            {[
              { value: '+50K', label: t('about.citizens_spain') },
              { value: '+1975', label: t('about.diplomatic_since') },
              { value: '10+', label: t('about.consular_services_count') },
            ].map(({ value, label }) => (
              <div key={label}>
                <p className="text-3xl font-bold text-[#FFD700]">{value}</p>
                <p className="text-xs text-red-100 mt-1">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Current Ambassador Section */}
      <div className="py-16 bg-white" id="embaixador" data-testid="ambassador-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10">
            <p className="text-xs text-[#CC0000] font-semibold uppercase tracking-widest mb-2">{t('about.head_of_mission')}</p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">{t('about.the_ambassador')}</h2>
            <div className="w-16 h-1 bg-[#CC0000] mx-auto" />
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Photo and Info */}
            <div className="lg:col-span-1">
              <div className="bg-gray-50 rounded-2xl p-6 text-center">
                <div className="relative inline-block mb-4">
                  <img 
                    src={AMBASSADOR.photo} 
                    alt={AMBASSADOR.name} 
                    className="w-48 h-48 rounded-full object-cover mx-auto border-4 border-[#FFD700] shadow-xl"
                  />
                  <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-[#CC0000] text-white px-4 py-1 rounded-full text-xs font-semibold">
                    <Award size={12} className="inline mr-1" />
                    {t('about.ambassador_label')}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">{AMBASSADOR.name}</h3>
                <p className="text-sm text-[#CC0000] font-medium mb-4">{AMBASSADOR.title}</p>
                <div className="flex justify-center gap-3">
                  <Link to="/contactos" className="inline-flex items-center gap-1 px-4 py-2 bg-[#CC0000] text-white text-sm font-medium rounded-md hover:bg-[#A30000] transition-colors">
                    <Mail size={14} /> {t('about.contact')}
                  </Link>
                </div>
              </div>
            </div>
            
            {/* Biography and Message */}
            <div className="lg:col-span-2 space-y-6">
              {/* Biography */}
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Users size={18} className="text-[#CC0000]" />
                  {t('about.biography')}
                </h4>
                <div className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">
                  {AMBASSADOR.biography}
                </div>
              </div>
              
              {/* Message */}
              <div className="bg-[#0A0A0A] text-white rounded-xl p-6">
                <div className="flex items-start gap-3">
                  <Quote size={28} className="text-[#FFD700] flex-shrink-0 rotate-180" />
                  <div>
                    <h4 className="font-bold text-[#FFD700] mb-3">{t('about.ambassador_message')}</h4>
                    <div className="text-sm text-gray-300 leading-relaxed whitespace-pre-line italic">
                      {AMBASSADOR.message}
                    </div>
                    <p className="mt-4 text-right text-[#FFD700] font-semibold">— {AMBASSADOR.name}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Former Ambassadors Slideshow */}
      <div className="py-16 bg-gray-50" id="antigos-embaixadores">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10">
            <p className="text-xs text-[#CC0000] font-semibold uppercase tracking-widest mb-2">{t('about.diplomatic_history')}</p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">{t('about.former_ambassadors')}</h2>
            <p className="text-gray-500 text-sm">{t('about.former_ambassadors_desc')}</p>
            <div className="w-16 h-1 bg-[#CC0000] mx-auto mt-3" />
          </div>
          
          <FormerAmbassadorsSlideshow />
        </div>
      </div>

      {/* Mission */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">{t('about.our_mission')}</h2>
            <div className="w-16 h-1 bg-[#CC0000] mx-auto" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Globe, title: t('about.diplomatic_mission'), text: t('about.diplomatic_mission_desc') },
              { icon: Shield, title: t('about.consular_protection'), text: t('about.consular_protection_desc') },
              { icon: Users, title: t('about.community_support'), text: t('about.community_support_desc') },
            ].map(({ icon: Icon, title, text }) => (
              <div key={title} className="text-center p-6 bg-gray-50 border border-gray-100 rounded-xl hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon size={22} className="text-[#CC0000]" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

    </div>
  );
}
